import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Time } from '~shared/ui/Time';
import {
    COUNT_DOWN_TIMER_CSS_BLOCK,
    COUNT_DOWN_TIMER_CLASS_WARNING_1,
    COUNT_DOWN_TIMER_CLASS_WARNING_2,
    COUNT_DOWN_TIMER_CLASS_WAIT,
    COUNT_DOWN_TIMER_WARNING_1,
    COUNT_DOWN_TIMER_WARNING_2,
    COUNT_DOWN_TIMER_ZERO,
    COUNT_DOWN_TIMER_INTERVAL,
    COUNT_DOWN_FINISHED,
} from './constants';
import './styles/count-down-timer.scss';
import {
    getCrossPlatformDateTime,
    setZeroMaskTimeInfo,
} from '../../../utils/date-time';
import {
    MILLI_SECONDS_UNIT,
    DAY_IN_HOURS,
    HOUR_IN_MINUTES,
} from '../../../utils/constants';

const propTypes = {
    scheduledTime: PropTypes.string.isRequired,
    timeWarnings: PropTypes.array,
    intervalTime: PropTypes.number,
    renderCleanTimer: PropTypes.bool,
    showFullDayCountdown: PropTypes.bool,
    styleClass: PropTypes.string,
};

const defaultProps = {
    intervalTime: COUNT_DOWN_TIMER_INTERVAL,
    timeWarnings: [
        {
            css: COUNT_DOWN_TIMER_CLASS_WAIT,
            time: COUNT_DOWN_TIMER_ZERO,
        },
        {
            css: COUNT_DOWN_TIMER_CLASS_WARNING_2,
            time: COUNT_DOWN_TIMER_WARNING_2,
        },
        {
            css: COUNT_DOWN_TIMER_CLASS_WARNING_1,
            time: COUNT_DOWN_TIMER_WARNING_1,
        },
    ],
    renderCleanTimer: false,
    showFullDayCountdown: false,
    styleClass: '',
};
/**
 * CountDownTimer
 *
 * CountDown Timer Component implementing a state collection, by default contains three but it can be extended
 * to more options.
 * The default states go from the highest to the lowest time
 *
 * SetInterval method has been implemented as part of the component.
 * The countdown starts as soon as is loaded in the browser.
 *
 * @param {String} scheduledTime Scheduled time
 * @param {Array} timeWarnings they are the collection of states
 * @param {Number} intervalTime How often the component will check for new updates in terms of the time
 *
 * @return {React} CountDownTimer component
 *
 * i.e.
 *
 *  <CountDownTimer scheduledTime='YYYY-MM-DD HH:mm:ss' />
 *
 */
class CountDownTimer extends React.PureComponent {
    constructor(props) {
        super(props);

        const maxWarningTime = this.props.timeWarnings.reduce((previous, next) => {
            return {
                time: Math.max(previous.time, next.time),
            };
        });

        this.state = {
            timeLabel: null,
            maxWarningTime,
        };

        this.defineState = this.defineState.bind(this);
    }

    componentDidMount() {
        const { intervalTime } = this.props;

        this.defineState();
        this.timer = setInterval(this.defineState, intervalTime);
    }

    componentWillUnmount() {
        this.endInterval();
    }

    endInterval() {
        clearInterval(this.timer);
    }

    static getDiffTime (time) {
        let result = {};
        const scheduledTime = Date.parse(getCrossPlatformDateTime(time).toUTCString());
        const currentTime = Date.parse(new Date().toUTCString());

        result.total = scheduledTime - currentTime;
        result.total_seconds = Math.floor(
            (result.total/MILLI_SECONDS_UNIT)
        );
        result.days = Math.floor(
            result.total/( MILLI_SECONDS_UNIT*HOUR_IN_MINUTES*HOUR_IN_MINUTES*DAY_IN_HOURS)
        );
        result.hours = Math.floor(
            (result.total/(MILLI_SECONDS_UNIT*HOUR_IN_MINUTES*HOUR_IN_MINUTES)) % DAY_IN_HOURS
        );
        result.minutes = Math.floor(
            (result.total/MILLI_SECONDS_UNIT/HOUR_IN_MINUTES) % HOUR_IN_MINUTES
        );
        result.seconds = Math.floor(
            result.total_seconds % HOUR_IN_MINUTES
        );

        return result;
    }

    defineState () {
        const { scheduledTime: time, timeWarnings, showFullDayCountdown } = this.props;
        const { maxWarningTime } = this.state;
        const zero = 0;
        const diffTime = CountDownTimer.getDiffTime(time);
        const diffInSeconds = parseInt(diffTime.total_seconds);
        const diffInDays = diffTime.days;
        const conditionTime = (maxWarningTime.time >= diffInSeconds && diffInSeconds >= zero);

        const warningState = timeWarnings.reduce((previous, next) => {
            if (previous.time >= diffInSeconds && conditionTime) {
                return previous;
            } else if (next.time >= diffInSeconds && conditionTime) {
                return next;
            }

            return {
                time: null,
            };
        });

        let timeLabel = false;
        const conditionZeroTime = (diffInSeconds <= zero);

        /**
         * When diffInDays is 0 means the day is today
         */
        if (diffInDays === zero && showFullDayCountdown) {
            const hoursLeft = setZeroMaskTimeInfo(diffTime.hours);
            const minutesLeft = setZeroMaskTimeInfo(diffTime.minutes);
            const secondsLeft = setZeroMaskTimeInfo(diffTime.seconds);
            timeLabel = `${hoursLeft}:${minutesLeft}:${secondsLeft}`;
        } else if (diffInDays === zero && conditionTime && !showFullDayCountdown) {
            const minutesLeft = setZeroMaskTimeInfo(diffTime.minutes);
            const secondsLeft = setZeroMaskTimeInfo(diffTime.seconds);
            timeLabel = `${minutesLeft}:${secondsLeft}`;
        } else if (conditionZeroTime) {
            /*
             * We need to check once time has counted down fully, but event still shows on load
             * that we show 00:00 instead of the scheduled start time
             */
            timeLabel = COUNT_DOWN_FINISHED;
        }

        this.setState({
            timeLabel,
            css: (conditionZeroTime ? COUNT_DOWN_TIMER_CLASS_WAIT : warningState.css),
        });

        if (diffInSeconds <= zero) {
            this.endInterval();
        }

        return this;
    }

    render () {
        const { css, timeLabel } = this.state;
        const { scheduledTime, styleClass } = this.props;

        const scheduledTimeMask = timeLabel !== false ? timeLabel : <Time dateTime={scheduledTime} />;

        const cssClass = classNames(
            COUNT_DOWN_TIMER_CSS_BLOCK,
            (css || ''),
            (styleClass || '')
        );

        if (this.props.renderCleanTimer) {
            return (scheduledTimeMask);
        }

        return (
            <div className={cssClass} data-scheduled-time={scheduledTime}>
                {scheduledTimeMask}
            </div>
        );
    }
}

CountDownTimer.propTypes = propTypes;
CountDownTimer.defaultProps = defaultProps;

export default CountDownTimer;
